import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  getAgentCabinetMe,
  getAgentCabinetReconciliation,
} from "../../api/agentCabinet";
import i18n from "../../i18n";

const formatUah = (value) => {
  const amount = Number(value || 0).toLocaleString("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return i18n.t("units.uahAmount", { amount });
};

const AgentDashboardPage = () => {
  const { t } = useTranslation("common");
  const [me, setMe] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const now = dayjs();
        const [meData, recon] = await Promise.all([
          getAgentCabinetMe(),
          getAgentCabinetReconciliation({
            year: now.year(),
            month: now.month() + 1,
          }),
        ]);
        if (!cancelled) {
          setMe(meData);
          setReport(recon);
        }
      } catch (e) {
        if (!cancelled) {
          setError(t("agent.loadError"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 6 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t("agent.dashboardTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {me?.display_name || "—"}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              {t("agent.commission")}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {me?.commission_percent ?? "0"}%
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              {t("agent.thisMonth")}
            </Typography>
            <Typography variant="body1">
              {t("agent.passengers")}: {report?.totals?.passengers ?? 0}
            </Typography>
            <Typography variant="body1">
              {t("agent.dueToYou")}: {formatUah(report?.totals?.due_to_agent_uah)}
            </Typography>
            <Typography variant="body1">
              {t("agent.dueFromYou")}:{" "}
              {formatUah(report?.totals?.due_from_agent_uah)}
            </Typography>
          </CardContent>
        </Card>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button
            component={RouterLink}
            to="/agent/bookings"
            variant="contained"
          >
            {t("agent.myBookings")}
          </Button>
          <Button
            component={RouterLink}
            to="/agent/reconciliation"
            variant="outlined"
          >
            {t("agent.reconciliation")}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
};

export default AgentDashboardPage;
